import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Play } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { Overline } from "@/components/Reveal";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const HeadlineLine = ({ children, i }) => (
  <span className="hero-line-mask">
    <motion.span
      custom={i}
      variants={line}
      initial="hidden"
      animate="visible"
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const vslY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const vslScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid={HERO.root}
      className="relative min-h-screen pt-32 pb-20 md:pt-40 overflow-hidden"
    >
      <div className="absolute inset-0 grid-lines opacity-60 pointer-events-none" />
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent via-transparent to-[#050505] pointer-events-none" />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <Overline>Automação de processos · Integração de sistemas</Overline>
        </motion.div>

        <h1 className="font-display font-extrabold text-[13vw] leading-[0.95] sm:text-6xl md:text-7xl lg:text-[5.6rem] xl:text-[6.4rem] tracking-tighter max-w-[16ch]">
          <HeadlineLine i={0}>Pare de usar</HeadlineLine>
          <HeadlineLine i={1}>
            <span className="text-neutral-500">pessoas</span> como
          </HeadlineLine>
          <HeadlineLine i={2}>integração entre</HeadlineLine>
          <HeadlineLine i={3}>sistemas.</HeadlineLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 grid md:grid-cols-[1fr_auto] gap-8 md:gap-16 items-end"
        >
          <div className="max-w-[54ch]">
            <p className="text-neutral-400 text-lg md:text-xl leading-relaxed">
              Conectamos WhatsApp, CRM, ERP, planilhas, APIs e até sistemas
              antigos sem integração, transformando tarefas manuais em processos
              automatizados e controlados.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-[11px] uppercase tracking-[0.18em] text-neutral-600">
              {["WhatsApp", "CRM", "ERP", "Planilhas", "APIs", "Sistemas legados"].map(
                (t, i) => (
                  <span key={t} className="flex items-center gap-3">
                    {i > 0 && <span className="text-neutral-800">·</span>}
                    <span className="hover:text-neutral-400 transition-colors">{t}</span>
                  </span>
                )
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row md:flex-col gap-3 shrink-0">
            <a
              href="/contato"
              data-testid={HERO.ctaPrimary}
              className="group inline-flex items-center justify-center gap-2 bg-white text-black font-semibold px-7 py-4 rounded-sm hover:bg-neutral-200 active:scale-95 transition-all"
            >
              Mapear meu processo
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#how"
              data-testid={HERO.ctaSecondary}
              className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-medium px-7 py-4 rounded-sm hover:border-white/50 hover:bg-white/5 active:scale-95 transition-all"
            >
              Entender como funciona
            </a>
          </div>
        </motion.div>

        {/* VSL placeholder */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-20 md:mt-28"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-neutral-600">
              VSL · em breve
            </span>
            <span className="h-px flex-1 bg-white/10" />
          </div>
          <div
            data-testid={HERO.vsl}
            className="relative w-full aspect-video border border-white/10 rounded-sm overflow-hidden group cursor-pointer"
          >
            <motion.img
              style={{ y: vslY, scale: vslScale }}
              src="https://images.unsplash.com/photo-1709625862266-014ef072fd93?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg2NzUxNTMzfDA&ixlib=rb-4.1.0&q=85"
              alt="Arquitetura de automação"
              className="absolute inset-0 w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-black/60" />
            <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
              <div className="h-16 w-16 md:h-20 md:w-20 rounded-full border border-white/30 flex items-center justify-center backdrop-blur-sm bg-white/5 group-hover:scale-110 group-hover:border-white/60 transition-all">
                <Play className="h-6 w-6 md:h-7 md:w-7 fill-white text-white ml-1" />
              </div>
              <p className="mt-6 max-w-[40ch] text-neutral-300 text-sm md:text-base">
                Veja como processos manuais podem ser automatizados sem
                reconstruir sua empresa do zero.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
